import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useSEO } from '../../../utils/seo';
import Icon from '../../../components/base/Icon';

const QUOTE_FORM_URL = 'https://readdy.ai/api/form/d6p540d39lnhn4hh1kf0';

const SERVICE_OPTIONS = [
  { value: '', label: 'Select a service...' },
  { value: 'Fence Installation', label: 'Fence Installation' },
  { value: 'Fence Repair', label: 'Fence Repair' },
  { value: 'Gate Installation', label: 'Gate Installation' },
  { value: 'Fence Replacement', label: 'Fence Replacement' },
  { value: 'Other Fence Work', label: 'Other Fence Work' },
];

interface QuoteFormState {
  full_name: string;
  phone: string;
  email: string;
  service_needed: string;
  project_details: string;
}

const emptyForm = (): QuoteFormState => ({
  full_name: '',
  phone: '',
  email: '',
  service_needed: '',
  project_details: '',
});

function LocationQuoteForm({ formId }: { formId: string }) {
  const [values, setValues] = useState<QuoteFormState>(emptyForm());
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setValues((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const validate = (): string => {
    if (!values.full_name.trim()) return 'Full name is required.';
    if (!values.phone.trim()) return 'Phone number is required.';
    if (!values.email.trim()) return 'Email address is required.';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.email)) return 'Please enter a valid email address.';
    if (!values.service_needed) return 'Please select a service.';
    if (!values.project_details.trim()) return 'Please describe your project.';
    return '';
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const validationError = validate();
    if (validationError) {
      setErrorMsg(validationError);
      setStatus('error');
      return;
    }
    setStatus('submitting');
    setErrorMsg('');
    try {
      const params = new URLSearchParams();
      (Object.keys(values) as (keyof QuoteFormState)[]).forEach((key) => {
        params.append(key, values[key]);
      });
      const res = await fetch(QUOTE_FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });
      if (!res.ok) throw new Error('Submission failed');
      setStatus('success');
      setValues(emptyForm());
    } catch {
      setStatus('error');
      setErrorMsg('Something went wrong. Please try again or call us at (208) 204-6331.');
    }
  };

  const inputClass = 'w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#B45309] focus:border-transparent text-sm';

  return (
    <form onSubmit={handleSubmit} data-readdy-form id={formId} noValidate>
      {status === 'success' && (
        <div className="mb-5 p-4 bg-green-50 border border-green-200 rounded-lg text-green-800 text-sm flex items-start gap-2" role="alert">
          <span className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5"><Icon name="check" className="w-4 h-4" /></span>
          <span>Thanks — we received your quote request and will contact you within 24 hours.</span>
        </div>
      )}
      {status === 'error' && errorMsg && (
        <div className="mb-5 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm flex items-start gap-2" role="alert">
          <span className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5"><Icon name="circle-exclamation" className="w-4 h-4" /></span>
          <span>{errorMsg}</span>
        </div>
      )}
      <div className="grid md:grid-cols-2 gap-4 mb-4">
        <div>
          <label htmlFor={`${formId}-full_name`} className="block text-sm font-medium text-gray-700 mb-1">Full Name *</label>
          <input type="text" id={`${formId}-full_name`} name="full_name" required value={values.full_name} onChange={handleChange} className={inputClass} placeholder="John Smith" />
        </div>
        <div>
          <label htmlFor={`${formId}-phone`} className="block text-sm font-medium text-gray-700 mb-1">Phone Number *</label>
          <input type="tel" id={`${formId}-phone`} name="phone" required value={values.phone} onChange={handleChange} className={inputClass} placeholder="(208) 555-0123" />
        </div>
      </div>
      <div className="mb-4">
        <label htmlFor={`${formId}-email`} className="block text-sm font-medium text-gray-700 mb-1">Email Address *</label>
        <input type="email" id={`${formId}-email`} name="email" required value={values.email} onChange={handleChange} className={inputClass} placeholder="john@example.com" />
      </div>
      <div className="mb-4">
        <label htmlFor={`${formId}-service_needed`} className="block text-sm font-medium text-gray-700 mb-1">Service Needed *</label>
        <select id={`${formId}-service_needed`} name="service_needed" required value={values.service_needed} onChange={handleChange} className={`${inputClass} cursor-pointer`}>
          {SERVICE_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      </div>
      <div className="mb-6">
        <label htmlFor={`${formId}-project_details`} className="block text-sm font-medium text-gray-700 mb-1">Project Details *</label>
        <textarea
          id={`${formId}-project_details`}
          name="project_details"
          required
          rows={3}
          maxLength={500}
          value={values.project_details}
          onChange={handleChange}
          className={`${inputClass} resize-none`}
          placeholder="Describe your fence project — material preference, linear footage, property type, neighborhood..."
        />
        <p className="text-xs text-gray-500 mt-1">{values.project_details.length}/500 characters</p>
      </div>
      <button
        type="submit"
        disabled={status === 'submitting'}
        className="w-full bg-[#B45309] hover:bg-[#92400E] disabled:opacity-60 text-white px-6 py-4 rounded-lg font-semibold transition-colors whitespace-nowrap cursor-pointer"
      >
        {status === 'submitting' ? 'Sending...' : 'Request Free Boise Fence Quote'}
      </button>
    </form>
  );
}

const commonRequests = [
  {
    icon: 'border-all',
    title: 'New privacy fence installation',
    desc: 'Homeowners in established Boise neighborhoods frequently request 6-foot cedar or vinyl privacy fences to define property lines, contain pets, and add backyard seclusion.',
  },
  {
    icon: 'triangle-exclamation',
    title: 'Storm and wind damage repair',
    desc: 'Boise\'s seasonal windstorms — particularly in spring and fall — regularly knock over fence panels and snap posts. We handle emergency repairs and full section replacements.',
  },
  {
    icon: 'rotate',
    title: 'Full fence replacement on older properties',
    desc: 'Many Boise homes built in the 1980s–2000s have aging wood fences that have reached the end of their useful life. We remove the old fence and install a new one in the same footprint.',
  },
  {
    icon: 'door-open',
    title: 'Gate installation and repair',
    desc: 'Walk-through and double drive gates are among the most common add-ons we install — both for new fence projects and as standalone upgrades to existing fences.',
  },
  {
    icon: 'file-contract',
    title: 'HOA-compliant fence upgrades',
    desc: 'Many Boise-area HOAs have specific rules on fence height, material, and color. We\'re familiar with common HOA requirements and can help you build a fence that passes review.',
  },
];

const internalLinks = [
  {
    icon: 'border-all',
    label: 'Fence Installation & Repair Service Page',
    desc: 'Full overview of materials, process, and what to expect.',
    to: '/services/fence',
  },
  {
    icon: 'tag',
    label: 'Fence Pricing Guide for Boise',
    desc: 'Real cost ranges for wood, vinyl, chain link, and gates.',
    to: '/blog/fence-installation-cost-boise-idaho',
  },
  {
    icon: 'clock',
    label: 'Fence Installation Timeline Guide',
    desc: 'How long installation takes and what can delay it.',
    to: '/blog/fence-installation-timeline-boise',
  },
  {
    icon: 'circle-question',
    label: 'Fence FAQs — Permits, HOA & More',
    desc: 'Answers to the most common questions Boise homeowners ask.',
    to: '/services/fence#faq-fence-0',
  },
  {
    icon: 'phone',
    label: 'Contact & Get a Free Quote',
    desc: 'Call (208) 204-6331 or submit a quote request online.',
    to: '#boise-fence-quote',
  },
];

const faqs = [
  {
    q: 'Do I need a permit to install a fence in Boise, Idaho?',
    a: 'In Boise, most rear and side yard fences under 6 feet tall do not require a building permit. However, front yard fences, fences over 6 feet, and fences in certain zoning districts may require one. Rules also vary by HOA. We recommend checking with the City of Boise Planning & Development Services before starting any project — and we\'re happy to help you navigate this during the estimate process.',
  },
  {
    q: 'How does Boise\'s climate affect fence materials?',
    a: 'Boise has a semi-arid climate with hot, dry summers and cold winters that occasionally dip below freezing. Cedar wood performs well here because it\'s naturally rot-resistant and handles temperature swings without warping as badly as pine. Vinyl is excellent for low maintenance but can become brittle in hard freezes. Chain link is the most weather-resistant overall. We\'ll recommend the right material based on your specific situation.',
  },
  {
    q: 'What are typical HOA fence rules in Boise neighborhoods?',
    a: 'HOA rules vary significantly by community. Common restrictions in Boise-area HOAs include maximum fence height (often 6 feet in back yards, 4 feet in front yards), approved materials (many require wood or vinyl — no chain link in front yards), color restrictions, and setback requirements from property lines. Some HOAs require written approval before installation. We\'re familiar with many local HOA requirements and can advise during your estimate.',
  },
  {
    q: 'How far in advance should I book a fence installation in Boise?',
    a: 'Spring and early summer (April through July) are our busiest months for fence installation in Boise. During peak season, we typically book 2–4 weeks out. Fall and winter have more availability and can be a good time to get on the schedule. We recommend reaching out as early as possible to lock in your preferred start date, especially if you have a deadline like a home sale or HOA compliance notice.',
  },
  {
    q: 'Do you handle utility locates before digging fence posts in Boise?',
    a: 'Yes — Idaho law requires calling 811 before any digging, and we coordinate this as part of every fence installation. The utility marking process typically takes 2–3 business days. We factor this into the project timeline so there are no surprises. Never skip this step — unmarked utility lines can cause serious safety hazards and costly damage.',
  },
  {
    q: 'Can you match the fence style of my existing fence or my neighbor\'s fence?',
    a: 'In most cases, yes. For wood fences, we can closely match board style, height, and stain or paint color. For vinyl, matching depends on whether the same panel profile is still available from current suppliers. Chain link is straightforward to match. Bring photos of the existing fence to your estimate appointment and we\'ll let you know what\'s achievable.',
  },
];

export default function FenceInstallationBoiseIdahoPage() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  useSEO({
    title: 'Fence Installation Boise Idaho | Fix Right Services — Licensed & Insured',
    description: 'Local fence installation and repair in Boise, Idaho. Wood, vinyl, chain link & gates. Serving North End, Bench, Southeast Boise & all neighborhoods. Free estimates. Call (208) 204-6331.',
    keywords: 'fence installation Boise Idaho, fence contractor Boise, wood fence Boise, vinyl fence Boise, fence repair Boise Idaho, gate installation Boise',
    canonical: `${import.meta.env.VITE_SITE_URL || 'https://fixrightservices.com'}/locations/fence-installation-boise-idaho`,
    schema: {
      '@context': 'https://schema.org',
      '@type': 'LocalBusiness',
      'name': 'Fix Right Services — Fence Installation Boise Idaho',
      'description': 'Professional fence installation and repair in Boise, Idaho. Wood, vinyl, chain link, and custom gates. Licensed and insured.',
      'telephone': '+12082046331',
      'email': 'Southernkole@gmail.com',
      'url': `${import.meta.env.VITE_SITE_URL || 'https://fixrightservices.com'}/locations/fence-installation-boise-idaho`,
      'address': {
        '@type': 'PostalAddress',
        'addressLocality': 'Boise',
        'addressRegion': 'ID',
        'addressCountry': 'US',
      },
      'geo': {
        '@type': 'GeoCoordinates',
        'latitude': 43.6150,
        'longitude': -116.2023,
      },
      'areaServed': {
        '@type': 'City',
        'name': 'Boise',
        'sameAs': 'https://en.wikipedia.org/wiki/Boise,_Idaho',
      },
      'hasOfferCatalog': {
        '@type': 'OfferCatalog',
        'name': 'Fence Services in Boise',
        'itemListElement': [
          { '@type': 'Offer', 'itemOffered': { '@type': 'Service', 'name': 'Wood Fence Installation Boise' } },
          { '@type': 'Offer', 'itemOffered': { '@type': 'Service', 'name': 'Vinyl Fence Installation Boise' } },
          { '@type': 'Offer', 'itemOffered': { '@type': 'Service', 'name': 'Chain Link Fence Boise' } },
          { '@type': 'Offer', 'itemOffered': { '@type': 'Service', 'name': 'Gate Installation Boise' } },
          { '@type': 'Offer', 'itemOffered': { '@type': 'Service', 'name': 'Fence Repair Boise Idaho' } },
        ],
      },
    },
  });

  return (
    <div className="min-h-screen bg-white">

      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-sm shadow-sm" aria-label="Main navigation">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <div className="flex items-center gap-8">
              <Link to="/">
                <img
                  src="https://static.readdy.ai/image/c174e3e7fa028bcf803982b5ec8aa9e6/2ea97a783d40b0afc97fadf5f7236f78.webp"
                  alt="Fix Right Services Logo"
                  width="180"
                  height="56"
                  className="h-14 w-auto"
                  loading="eager"
                />
              </Link>
              <div className="hidden md:flex items-center gap-6 text-sm font-medium text-gray-700">
                <Link to="/" className="hover:text-[#B45309] transition-colors cursor-pointer">Home</Link>
                <Link to="/services" className="hover:text-[#B45309] transition-colors cursor-pointer">Services</Link>
                <Link to="/services/fence" className="text-[#B45309] font-semibold cursor-pointer">Fence</Link>
                <Link to="/blog" className="hover:text-[#B45309] transition-colors cursor-pointer">Blog</Link>
              </div>
            </div>
            <a
              href="tel:+12082046331"
              className="flex items-center gap-2 bg-[#B45309] hover:bg-[#92400E] text-white px-6 py-3 rounded-lg font-semibold transition-colors whitespace-nowrap cursor-pointer"
              aria-label="Call Fix Right Services"
            >
              <span className="w-5 h-5 flex items-center justify-center"><Icon name="phone" className="w-4 h-4" /></span>
              <span className="hidden sm:inline">(208) 204-6331</span>
              <span className="sm:hidden">Call Now</span>
            </a>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative pt-32 pb-24 px-4 sm:px-6 lg:px-8 overflow-hidden" aria-label="Location page hero">
        <div className="absolute inset-0 z-0">
          <img
            src="https://public.readdy.ai/ai/img_res/441dedbbec10f8e780205e74868944f1.jpg"
            alt="Fence Installation in Boise Idaho neighborhoods"
            width="1920"
            height="750"
            className="w-full h-full object-cover object-top"
            loading="eager"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/45 to-black/65"></div>
        </div>
        <div className="relative z-10 max-w-5xl mx-auto w-full">
          <nav aria-label="Breadcrumb" className="mb-6">
            <ol className="flex items-center gap-2 text-sm text-white/70 flex-wrap">
              <li><Link to="/" className="hover:text-white transition-colors cursor-pointer">Home</Link></li>
              <li><span className="w-4 h-4 inline-flex items-center justify-center"><Icon name="chevron-right" className="w-3 h-3" /></span></li>
              <li><Link to="/services/fence" className="hover:text-white transition-colors cursor-pointer">Fence Services</Link></li>
              <li><span className="w-4 h-4 inline-flex items-center justify-center"><Icon name="chevron-right" className="w-3 h-3" /></span></li>
              <li className="text-white font-medium">Boise, Idaho</li>
            </ol>
          </nav>
          <div className="inline-flex items-center gap-2 bg-[#B45309]/90 text-white text-sm font-medium px-4 py-2 rounded-full mb-6">
            <span className="w-4 h-4 flex items-center justify-center"><Icon name="location-dot" className="w-3.5 h-3.5" /></span>
            Boise, Idaho — Local Fence Contractor
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
            Fence Installation in Boise, Idaho
          </h1>
          <p className="text-xl text-white/90 mb-8 max-w-3xl leading-relaxed">
            Fix Right Services installs and repairs fences for Boise homeowners across every neighborhood — from the North End and Bench to Southeast Boise and the East End. Licensed, insured, and locally operated.
          </p>
          <div className="flex flex-wrap gap-4 mb-10">
            <a
              href="tel:+12082046331"
              className="inline-flex items-center gap-2 bg-[#B45309] hover:bg-[#92400E] text-white px-8 py-3.5 rounded-lg text-lg font-semibold transition-colors whitespace-nowrap cursor-pointer"
            >
              <span className="w-5 h-5 flex items-center justify-center"><Icon name="phone" className="w-4 h-4" /></span>
              Call (208) 204-6331
            </a>
            <a
              href="#boise-fence-quote"
              className="inline-flex items-center gap-2 bg-white hover:bg-gray-50 text-gray-900 px-8 py-3.5 rounded-lg text-lg font-semibold transition-colors whitespace-nowrap cursor-pointer"
            >
              <span className="w-5 h-5 flex items-center justify-center"><Icon name="file-lines" className="w-4 h-4" /></span>
              Get Free Estimate
            </a>
          </div>
          <div className="flex flex-wrap gap-6 text-white/85 text-sm">
            {[
              { icon: 'clock', text: 'Response within 24 hours' },
              { icon: 'shield-halved', text: 'Licensed & Insured in Idaho' },
              { icon: 'tag', text: 'Free written estimates' },
              { icon: 'location-dot', text: 'Locally operated in Boise' },
            ].map((item) => (
              <div key={item.text} className="flex items-center gap-2">
                <span className="w-5 h-5 flex items-center justify-center"><Icon name={item.icon} className="w-4 h-4 text-[#F59E0B]" /></span>
                {item.text}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Localized Intro */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white" aria-label="About fence installation in Boise">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
                Serving Boise Homeowners Across Every Neighborhood
              </h2>
              <div className="space-y-4 text-gray-600 leading-relaxed">
                <p>
                  Boise is a city of distinct neighborhoods — each with its own character, lot sizes, and HOA rules. The <strong>North End</strong> is known for its mature trees, older homes, and narrower lots where fence placement requires careful attention to property lines. The <strong>Bench</strong> has a mix of mid-century homes and newer builds, often with larger backyards suited for full privacy fence installs. <strong>Southeast Boise</strong> and the <strong>East End</strong> tend to have newer subdivisions with active HOAs that have specific material and height requirements.
                </p>
                <p>
                  We work in all of these areas regularly. That means we understand the local permit landscape, common HOA restrictions, and the soil and terrain conditions that affect how fence posts are set. Boise&apos;s rocky, clay-heavy soil in some areas requires extra care during post installation — something a contractor unfamiliar with the area might not anticipate.
                </p>
                <p>
                  Whether you&apos;re in a newer subdivision near <strong>Boise State University</strong>, a historic block in the <strong>Hyde Park</strong> area, or a larger property on the outskirts near <strong>Boise Foothills</strong>, we bring the same level of care and craftsmanship to every project.
                </p>
              </div>
            </div>
            <div className="rounded-2xl overflow-hidden shadow-lg h-96">
              <img
                src="https://readdy.ai/api/search-image?query=professional%20fence%20contractor%20installing%20cedar%20wood%20privacy%20fence%20in%20a%20Boise%20Idaho%20residential%20backyard%20with%20mature%20trees%20green%20lawn%20warm%20afternoon%20sunlight%20craftsman%20home%20neighborhood%20quality%20workmanship&width=800&height=600&seq=loc-boise-fence-intro-01&orientation=portrait"
                alt="Fence installation in a Boise Idaho neighborhood"
                width="800"
                height="600"
                className="w-full h-full object-cover object-top"
                loading="lazy"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Common Requests */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50" aria-label="Common fence requests in Boise">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-14">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">Common Fence Requests in Boise</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              These are the fence projects we handle most often for Boise homeowners — based on what we actually see in the field.
            </p>
          </div>
          <div className="space-y-5">
            {commonRequests.map((item, idx) => (
              <div key={idx} className="bg-white rounded-2xl p-6 border border-gray-200 hover:shadow-md transition-shadow flex gap-5 items-start">
                <div className="w-12 h-12 flex items-center justify-center bg-[#B45309]/10 rounded-xl flex-shrink-0">
                  <Icon name={item.icon} className="w-6 h-6 text-[#B45309]" />
                </div>
                <div>
                  <h3 className="font-bold text-gray-900 mb-1">{item.title}</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Internal Links */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white" aria-label="Fence resources and guides">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">Everything You Need to Know</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Use these resources to research your fence project before reaching out — or jump straight to getting a quote.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {internalLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className="group bg-gray-50 hover:bg-[#B45309]/5 border border-gray-200 hover:border-[#B45309]/30 rounded-2xl p-6 transition-all cursor-pointer"
              >
                <div className="w-10 h-10 flex items-center justify-center bg-[#B45309]/10 rounded-lg mb-4 group-hover:bg-[#B45309]/20 transition-colors">
                  <Icon name={link.icon} className="w-5 h-5 text-[#B45309]" />
                </div>
                <h3 className="font-bold text-gray-900 text-sm mb-2 group-hover:text-[#B45309] transition-colors">{link.label}</h3>
                <p className="text-gray-500 text-xs leading-relaxed">{link.desc}</p>
                <span className="inline-flex items-center gap-1 text-[#B45309] text-xs font-semibold mt-3">
                  View
                  <span className="w-4 h-4 flex items-center justify-center"><Icon name="arrow-right" className="w-3 h-3" /></span>
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Trust Block */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50" aria-label="Licensing and insurance verification">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">Licensed, Insured &amp; Verifiable</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Before hiring any contractor in Idaho, you have the right to verify their license and confirm they carry insurance. Here&apos;s how to check ours.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-10">
            {/* Registration */}
            <div className="bg-white rounded-2xl border border-gray-200 p-8">
              <div className="flex items-start gap-4 mb-5">
                <div className="w-12 h-12 flex items-center justify-center bg-green-50 rounded-xl flex-shrink-0">
                  <Icon name="id-card" className="w-6 h-6 text-green-600" />
                </div>
                <div>
                  <h3 className="font-bold text-gray-900 text-lg">Idaho Contractor Registration</h3>
                  <p className="text-gray-500 text-sm">Verify our registration with the State of Idaho</p>
                </div>
              </div>
              <div className="bg-gray-50 rounded-xl p-4 mb-5">
                <p className="text-xs text-gray-500 uppercase tracking-wide font-semibold mb-1">Registration Number</p>
                <p className="text-2xl font-bold text-gray-900 tracking-wide">RCE-56789</p>
                <p className="text-xs text-gray-400 mt-1">Placeholder — replace with your actual Idaho registration number</p>
              </div>
              <div className="space-y-3 text-sm text-gray-600">
                <p>Idaho requires contractors to register with the <strong>Idaho Contractors Board</strong> before performing work. You can verify any contractor&apos;s registration status online at no cost.</p>
                <p className="font-medium text-gray-700">How to verify:</p>
                <ol className="space-y-1 list-decimal list-inside text-gray-600">
                  <li>Visit the Idaho Division of Building Safety website</li>
                  <li>Navigate to &quot;Contractor License Lookup&quot;</li>
                  <li>Search by company name: <strong>Fix Right Services</strong></li>
                  <li>Confirm registration status is active</li>
                </ol>
              </div>
              <a
                href="https://dbs.idaho.gov/contractor-registration/"
                target="_blank"
                rel="nofollow noopener noreferrer"
                className="inline-flex items-center gap-2 mt-5 bg-green-50 hover:bg-green-100 text-green-700 border border-green-200 px-4 py-2.5 rounded-lg text-sm font-semibold transition-colors whitespace-nowrap cursor-pointer"
              >
                <span className="w-4 h-4 flex items-center justify-center"><Icon name="arrow-up-right-from-square" className="w-3.5 h-3.5" /></span>
                Idaho Contractor Lookup
              </a>
            </div>

            {/* Insurance */}
            <div className="bg-white rounded-2xl border border-gray-200 p-8">
              <div className="flex items-start gap-4 mb-5">
                <div className="w-12 h-12 flex items-center justify-center bg-amber-50 rounded-xl flex-shrink-0">
                  <Icon name="shield-halved" className="w-6 h-6 text-[#B45309]" />
                </div>
                <div>
                  <h3 className="font-bold text-gray-900 text-lg">General Liability Insurance</h3>
                  <p className="text-gray-500 text-sm">Coverage details and how to request a certificate</p>
                </div>
              </div>
              <div className="bg-amber-50 rounded-xl p-4 mb-5 border border-amber-100">
                <p className="text-xs text-[#B45309] uppercase tracking-wide font-semibold mb-2">Coverage Summary</p>
                <ul className="space-y-1.5 text-sm text-gray-700">
                  <li className="flex items-start gap-2">
                    <span className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5"><Icon name="check" className="w-3.5 h-3.5 text-green-600" /></span>
                    General Liability Insurance — active
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5"><Icon name="check" className="w-3.5 h-3.5 text-green-600" /></span>
                    Covers property damage and bodily injury
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5"><Icon name="check" className="w-3.5 h-3.5 text-green-600" /></span>
                    Certificate of insurance available on request
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5"><Icon name="check" className="w-3.5 h-3.5 text-green-600" /></span>
                    Idaho-compliant coverage limits
                  </li>
                </ul>
                <p className="text-xs text-gray-400 mt-3">Placeholder — update with your actual policy details and carrier name</p>
              </div>
              <div className="text-sm text-gray-600 space-y-2">
                <p>We carry general liability insurance on all fence installation and repair work performed in Idaho. You can request a <strong>Certificate of Insurance (COI)</strong> before any work begins — just ask when you call or submit your quote request.</p>
                <p className="text-xs text-gray-500 italic">Hiring an uninsured contractor can leave you personally liable for accidents or damage that occur on your property.</p>
              </div>
              <a
                href="tel:+12082046331"
                className="inline-flex items-center gap-2 mt-5 bg-[#B45309]/10 hover:bg-[#B45309]/20 text-[#B45309] border border-[#B45309]/20 px-4 py-2.5 rounded-lg text-sm font-semibold transition-colors whitespace-nowrap cursor-pointer"
              >
                <span className="w-4 h-4 flex items-center justify-center"><Icon name="phone" className="w-3.5 h-3.5" /></span>
                Request Certificate of Insurance
              </a>
            </div>
          </div>

          {/* Why it matters */}
          <div className="bg-white border border-gray-200 rounded-2xl p-6">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 flex items-center justify-center bg-amber-50 rounded-lg flex-shrink-0">
                <Icon name="triangle-exclamation" className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <h3 className="font-bold text-gray-900 mb-2">Why This Matters for Boise Homeowners</h3>
                <p className="text-gray-600 text-sm leading-relaxed">
                  Idaho does not require contractors to be licensed in the same way some states do, but it does require <strong>registration with the Idaho Contractors Board</strong> for most construction work. Always ask for proof of registration and insurance before signing any contract. An unregistered or uninsured contractor may offer a lower price — but if something goes wrong, you could be left with no recourse and potential liability on your own property.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Banner */}
      <section className="py-14 px-4 sm:px-6 lg:px-8 bg-[#B45309]" aria-label="Call to action">
        <div className="max-w-4xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-6">
          <div>
            <p className="text-white font-bold text-xl mb-1">Ready to get your Boise fence project started?</p>
            <p className="text-white/85 text-sm">Free on-site estimates. Response within 24 hours. No pressure.</p>
          </div>
          <div className="flex flex-wrap gap-3 flex-shrink-0">
            <a
              href="tel:+12082046331"
              className="inline-flex items-center gap-2 bg-white hover:bg-gray-50 text-[#B45309] px-6 py-3 rounded-lg font-semibold transition-colors whitespace-nowrap cursor-pointer"
            >
              <span className="w-5 h-5 flex items-center justify-center"><Icon name="phone" className="w-4 h-4" /></span>
              Call (208) 204-6331
            </a>
            <a
              href="#boise-fence-quote"
              className="inline-flex items-center gap-2 border-2 border-white text-white hover:bg-white/10 px-6 py-3 rounded-lg font-semibold transition-colors whitespace-nowrap cursor-pointer"
            >
              Request Free Quote
            </a>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white" aria-label="Boise fence FAQs">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">Fence FAQs Specific to Boise, Idaho</h2>
            <p className="text-gray-600">Questions we hear most often from Boise homeowners — answered honestly.</p>
          </div>
          <div className="space-y-3">
            {faqs.map((faq, idx) => (
              <div key={idx} className="bg-gray-50 border border-gray-200 rounded-xl overflow-hidden">
                <button
                  type="button"
                  onClick={() => setOpenFaq(openFaq === idx ? null : idx)}
                  className="w-full flex items-center justify-between px-6 py-5 text-left hover:bg-gray-100 transition-colors cursor-pointer"
                  aria-expanded={openFaq === idx}
                >
                  <h4 className="font-semibold text-gray-900 text-sm pr-4">
                    <a id={`faq-boise-${idx}`}>{faq.q}</a>
                  </h4>
                  <span className="w-6 h-6 flex items-center justify-center flex-shrink-0 text-[#B45309]">
                    <Icon name={openFaq === idx ? 'chevron-up' : 'chevron-down'} className="w-4 h-4" />
                  </span>
                </button>
                {openFaq === idx && (
                  <div className="px-6 pb-5">
                    <div className="border-t border-gray-200 pt-4">
                      <p className="text-gray-600 text-sm leading-relaxed">{faq.a}</p>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>

          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: JSON.stringify({
                '@context': 'https://schema.org',
                '@type': 'FAQPage',
                'mainEntity': faqs.map((faq) => ({
                  '@type': 'Question',
                  'name': faq.q,
                  'acceptedAnswer': {
                    '@type': 'Answer',
                    'text': faq.a,
                  },
                })),
              }),
            }}
          />
        </div>
      </section>

      {/* Quote Form */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50" id="boise-fence-quote" aria-label="Request a fence quote in Boise">
        <div className="max-w-2xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">Get a Free Fence Estimate in Boise</h2>
            <p className="text-gray-600">
              Tell us about your project and we&apos;ll get back to you within 24 hours with a free, no-obligation estimate.
            </p>
            <div className="flex items-center justify-center gap-2 mt-4 text-sm text-gray-500">
              <span className="w-5 h-5 flex items-center justify-center"><Icon name="phone" className="w-4 h-4 text-[#B45309]" /></span>
              Prefer to call?&nbsp;
              <a href="tel:+12082046331" className="text-[#B45309] font-semibold hover:underline cursor-pointer">(208) 204-6331</a>
            </div>
          </div>
          <div className="bg-white rounded-2xl border border-gray-200 p-8 shadow-sm">
            <LocationQuoteForm formId="boise-fence-location-quote" />
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <img src="https://static.readdy.ai/image/c174e3e7fa028bcf803982b5ec8aa9e6/2ea97a783d40b0afc97fadf5f7236f78.webp" alt="Fix Right Services" width="180" height="48" className="h-12 w-auto mb-4" loading="lazy" />
              <p className="text-gray-400 text-sm">Boise&apos;s trusted fence installation and repair experts serving the entire Treasure Valley.</p>
            </div>
            <div>
              <h3 className="font-bold text-lg mb-4">Fence Services</h3>
              <ul className="space-y-2 text-gray-400 text-sm">
                <li><Link to="/services/fence" className="hover:text-white transition-colors cursor-pointer">Fence Installation &amp; Repair</Link></li>
                <li><Link to="/blog/fence-installation-cost-boise-idaho" className="hover:text-white transition-colors cursor-pointer">Fence Pricing Guide</Link></li>
                <li><Link to="/blog/fence-installation-timeline-boise" className="hover:text-white transition-colors cursor-pointer">Timeline Guide</Link></li>
                <li><Link to="/blog/wood-vs-vinyl-fence-treasure-valley" className="hover:text-white transition-colors cursor-pointer">Wood vs. Vinyl Guide</Link></li>
              </ul>
            </div>
            <div>
              <h3 className="font-bold text-lg mb-4">Resources</h3>
              <ul className="space-y-2 text-sm">
                <li><Link to="/" className="text-gray-400 hover:text-white transition-colors cursor-pointer">Home</Link></li>
                <li><Link to="/services" className="text-gray-400 hover:text-white transition-colors cursor-pointer">All Services</Link></li>
                <li><Link to="/blog" className="text-gray-400 hover:text-white transition-colors cursor-pointer">Blog &amp; Tips</Link></li>
              </ul>
            </div>
            <div>
              <h3 className="font-bold text-lg mb-4">Contact</h3>
              <ul className="space-y-3 text-sm">
                <li className="flex items-start gap-2 text-gray-400">
                  <span className="w-4 h-4 flex items-center justify-center mt-0.5"><Icon name="phone" className="w-3.5 h-3.5 text-[#F59E0B]" /></span>
                  <a href="tel:+12082046331" className="hover:text-white transition-colors cursor-pointer">(208) 204-6331</a>
                </li>
                <li className="flex items-start gap-2 text-gray-400">
                  <span className="w-4 h-4 flex items-center justify-center mt-0.5"><Icon name="envelope" className="w-3.5 h-3.5 text-[#F59E0B]" /></span>
                  <a href="mailto:Southernkole@gmail.com" className="hover:text-white transition-colors cursor-pointer">Southernkole@gmail.com</a>
                </li>
                <li className="flex items-start gap-2 text-gray-400">
                  <span className="w-4 h-4 flex items-center justify-center mt-0.5"><Icon name="location-dot" className="w-3.5 h-3.5 text-[#F59E0B]" /></span>
                  <span>Boise, Meridian, Nampa &amp; Treasure Valley</span>
                </li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 pt-8 flex flex-col sm:flex-row justify-between items-center gap-4 text-sm text-gray-400">
            <p>&copy; 2025 Fix Right Services. All rights reserved.</p>
            <a href="https://readdy.ai/?ref=logo" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors cursor-pointer">Website Builder</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
